import { ReactNode, useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useAnalyticsCategory, useTrack } from '@app/analytics';
import { Modal } from './Modal';
import classnames from 'classnames';
import { SquareButton } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    width: theme.maxContentWidth.largeReduced,
    backgroundColor: theme.colors.white,
    zIndex: 1,
    padding: theme.marginBase * 4,
    borderRadius: theme.borderRadius.std,
    borderStyle: 'none',
    maxHeight: '90vh',
    outline: 'none',

    paddingBottom: 0,
    '& > *:last-child': {
      marginBottom: theme.marginBase * 4,
    },
    [theme.media.mobile]: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: 'auto',
      borderRadius: 0,
      maxHeight: 'none',
    },
  },
  large: {
    width: theme.marginBase * 90,
  },
  xlarge: {
    width: theme.maxContentWidth.largeModale,
  },
  xxlarge: {
    width: theme.maxContentWidth.hugeBlock,
  },
  overlflow: {
    overflow: 'visible',
  },
  titleLine: {
    display: 'flex',
    marginBottom: theme.marginBase * 3,
    alignItems: 'center',
  },
  titleSide: {
    marginRight: theme.marginBase * 4,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    marginRight: theme.marginBase,
    textTransform: 'capitalize',
  },
  title: {
    ...theme.fonts.h4,
  },
  text: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
  },
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase * 2,
    '& > *': {
      marginLeft: theme.marginBase,
    },
  },
  spacer: {
    flex: 1,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose?: () => any;
  children?: ReactNode;
  buttons?: ReactNode | ReactNode[];
  chip?: ReactNode | ReactNode[];
  leftButtons?: ReactNode | ReactNode[];
  title?: ReactNode;
  text?: ReactNode;
  large?: boolean;
  xlarge?: boolean;
  xxlarge?: boolean;
  id?: string;
  overflow?: boolean;
  className?: string;
  overlayClassName?: string;
  trackingName?: string;
}

export const CenteredModal = (props: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });
  const category = useAnalyticsCategory();
  const track = useTrack();

  const prevIsOpen = useRef<Boolean>(false);
  useEffect(() => {
    if (prevIsOpen.current === props.isOpen) {
      return;
    }
    prevIsOpen.current = props.isOpen;

    if (props.isOpen) {
      track(
        props.trackingName
          ? `${props.trackingName} Modal opened`
          : 'Modal Opened',
        {
          label: props.title,
        },
      );
    } else {
      track(
        props.trackingName
          ? `${props.trackingName} Modal closed`
          : 'Modal Closed',
        {
          label: props.title,
        },
      );
    }
  }, [props.isOpen, props.id, category, props.title, prevIsOpen]);

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      className={classnames(classes.modal, props.className, {
        [classes.large]: props.large,
        [classes.xlarge]: props.xlarge,
        [classes.xxlarge]: props.xxlarge,
        [classes.overlflow]: props.overflow,
      })}
      overlayClassName={classnames(classes.overlay, props.overlayClassName)}
    >
      <div className={classes.titleLine}>
        <div className={classes.titleSide}>
          {props.chip && <div className={classes.chip}>{props.chip}</div>}
          <h3 className={classes.title}>{props.title}</h3>
        </div>
        {props.onRequestClose && (
          <SquareButton
            icon="ri-close-line"
            color="ink"
            onClick={props.onRequestClose}
          />
        )}
      </div>
      <div>
        {!!props.text && <p className={classes.text}>{props.text}</p>}
        {props.children}
      </div>
      {(props.buttons || props.leftButtons) && (
        <div className={classes.buttonsLine}>
          {props.leftButtons}
          <div className={classes.spacer} />
          {props.buttons}
        </div>
      )}
    </Modal>
  );
};
